# Phase 3: Apply & Talent Search

import { Alert, Text } from 'braid-design-system';

<Alert tone="info">
  <Text>
    This phase is only applicable to existing integrations with our legacy
    Jobstreet & Jobsdb APIs. You may skip this section if you are only
    integrated with the SEEK API.
  </Text>
</Alert>

## Apply

Your software must comply with our apply product strategy to release into new markets.
We currently support two products:

1. [Optimised Apply](#optimised-apply)

   Candidates complete SEEK’s Apply Form without leaving our website or app.
   The SEEK API notifies you when an application is ready for export,
   and hirers can continue to manage applicants within your software.

2. [Apply with SEEK](#apply-with-seek)

   Candidates link out to an external careers website to submit their application,
   with an option to pre-fill fields using their SEEK Profile.

We have outlined the product options that you should offer going forward in the table below.
The strategy differs based on what you previously built for the Australia & New Zealand markets,
and in essence, you can release existing apply products across the Asia Pacific with minimal effort.

import { IconClear, IconTick } from 'braid-design-system';

|    Optimised Apply in ANZ?    |    Apply with SEEK in ANZ?    | Product options for APAC                                                                                                                                |
| :---------------------------: | :---------------------------: | :------------------------------------------------------------------------------------------------------------------------------------------------------ |
| <IconTick tone="positive" />  | <IconTick tone="positive" />  | <ol><li><p>Revisit [questionnaire options] and offer both products</p></li></ol>                                                                        |
| <IconTick tone="positive" />  | <IconClear tone="neutral" />  | <ol><li><p>Revisit [questionnaire options] and offer Optimised Apply only</p></li></ol>                                                                 |
| <IconClear tone="neutral" />  | <IconTick tone="positive" />  | <ol><li><p>Build Optimised Apply and offer both products</p><p><strong>or</strong></p></li><li><p>Offer Apply with SEEK only</p></li></ol>              |
| <IconClear tone="critical" /> | <IconClear tone="critical" /> | <ol><li><p>Build and offer Optimised Apply only</p><p><strong>or</strong></p></li><li><p>Commit to build an apply product after migration</p></li></ol> |

Some Jobstreet and Jobsdb hirers receive applications via email, which is no longer supported in the SEEK API.
SEEK instructed the affected hirers to use our employer website to screen applicants or link out to an external careers website,
and managed the change as a separate prerequisite to the [hirer transition].

[hirer transition]: /migration-guides/jobstreet-and-jobsdb-uplift/phase-4-transitioning-hirers

### Optimised Apply

[Optimised Apply](/use-cases/application-export) works in new markets without significant changes.
If you have implemented this product in Australia & New Zealand,
we recommend revisiting the [questionnaire options].
SEEK’s Screening Questions are a highly requested feature and we recently added a simpler integration option in the [Questionnaire Panel].

SEEK Profile information has been migrated from our Jobstreet & Jobsdb brands,
and depth may be limited until candidates review and update their profiles.
For example, structured [education history] fields were not migrated for Jobstreet candidate profiles.

The SEEK API does not translate user input and serves SEEK Profile information as supplied by the candidate.
As a result, your software may need to handle multilingual candidate data.

[education history]: /schema/named-type/CandidateProfile#/field/education
[questionnaire options]: /migration-guides/jobstreet-and-jobsdb-uplift/phase-2-job-posting/questionnaires
[Questionnaire Panel]: /use-cases/job-posting/questionnaires/v2/panel

### Apply with SEEK

The legacy Apply with SEEK product works in new markets without significant changes.

The SEEK API does not translate user input and serves SEEK Profile information as supplied by the candidate.
As a result, your software may need to handle multilingual candidate data.

## Talent Search

[Talent Search Connect](/use-cases/proactive-sourcing) is not yet available for hirer accounts registered in markets outside of Australia & New Zealand.

However, existing integrations benefit from a unified SEEK platform.
Hirers can [purchase profiles] and synchronise [uploaded candidates] across an APAC candidate pool.

The SEEK API does not translate user input and serves SEEK Profile information as supplied by the candidate.
As a result, your software may need to handle multilingual candidate data.

[purchase profiles]: /use-cases/proactive-sourcing/profile-purchases
[uploaded candidates]: /use-cases/proactive-sourcing/sharing-a-candidate
